/*
 * Copyright (C) Fraunhofer IESE 2021-2024 - Pedram (Majid) Jokar, Mher Ter-Tovmasyan,
 * Bestin John, Steffen Hupp, Anna Kleiner, Rafael Aranda Lopez King, Emily Calvet, Philipp Ewen,
 * Matthias Gerbershagen, Milad Chatrangoon, Stefan Schweitzer
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

export const toastsEn = {
  save: {
    success: { title: 'Successfully applied!' }
  },
  passwordReset: {
    success: {
      title: 'Email sent!',
      content:
        "We have sent an email with instructions to {{email}}. If you haven't received the message, please check your spam folder or request a new email."
    },
    error: {
      title: 'Oops, something went wrong!',
      content: 'The email could not be sent. Please try again later.'
    }
  },
  deactivateSecurity: {
    success: {
      title: '2FA deactivated',
      content: 'You can now log in without an additional code.'
    }
  },
  sendVerificationEmail: {
    success: { title: 'Email sent!' }
  },
  emailChangeCancel: {
    success: {
      title: 'Email address change successfully cancelled.'
    }
  },
  emailChangeResend: {
    success: { title: 'Email resent.' }
  },
  emailChange: {
    invalidCredentials: 'Please check your password and try again.',
    invalidCredentialsOrOtp:
      'Please check your password or security code and try again.',
    error: {
      title: 'Oops, something went wrong!',
      content:
        'The address {{email}} is already in use. Please try a different email address'
    }
  },
  requestDataReport: {
    success: {
      title: 'Data report requested',
      content: 'You will receive an email from us within 48 hours!'
    }
  },
  createOrganization: {
    success: { title: 'Organization was created.' }
  },
  leaveOrganization: {
    success: { title: 'Organization was left.' }
  },
  deleteOrganization: {
    success: { title: 'Successfully deleted!' },
    blocked: {
      title: 'Organization cannot be deleted',
      content:
        'There are still offers, booking requests or bookings remaining. Please remove or close them first.'
    }
  },
  deleteAccount: {
    blocked: {
      title: 'Account cannot be deleted',
      content:
        'There are still offers, booking requests or bookings in your organizations. Please remove or close them first.'
    },
    blockedAsLastPlatformAdmin: {
      title: 'Account cannot be deleted',
      content:
        'You are the only active portal administrator. Assign another administrator first.'
    }
  },
  loadOrganizationMembers: {
    error: {
      title: 'Oops, something went wrong!',
      content: 'Unfortunately, an error occurred while loading the member data.'
    }
  },
  updateMemberRole: {
    success: { title: 'Role was changed.' },
    error: { title: 'Role was not changed!' }
  },
  removeMember: {
    success: { title: 'Member was removed.' },
    error: { title: 'Member was not removed!' }
  },
  resendInvitation: {
    success: {
      title: 'Invitation was sent.',
      content: 'The email with the invitation link will arrive shortly.'
    },
    warning: {
      title: 'Invitation not sent!',
      content:
        'The previous invitation is still valid. For a new invitation, please try again later.'
    },
    error: {
      title: 'Oops, something went wrong!',
      content: 'The invitation could not be sent. Please try again later.'
    }
  },
  loadInvitations: {
    error: {
      title: 'Oops, something went wrong!',
      content:
        'Unfortunately, an error occurred while loading the invitation data.'
    }
  },
  createInvitation: {
    success: {
      title: 'Invitation was sent.',
      content:
        'The person with the email address {{email}} has been successfully invited to the organization.'
    },
    error: {
      title: 'Invitation not sent!',
      alreadyMember:
        'The person with the email address {{email}} is already a member of the organization.',
      alreadyInvited:
        'The person with the email address {{email}} has already been invited to the organization. Please ask the person to accept the invitation at {{url}}.'
    }
  },
  updateInvitationRole: {
    success: { title: 'Role was changed.' },
    error: { title: 'Role not changed.' }
  },
  revokeInvitation: {
    success: { title: 'Invitation was revoked.' },
    error: { title: 'Invitation not revoked!' }
  },
  acceptPendingInvitation: {
    error: {
      title: 'Invitation could not be accepted!',
      content: 'The invitation is unfortunately no longer valid.'
    }
  },
  rejectPendingInvitation: {
    error: {
      title: 'Invitation could not be rejected!',
      content: 'The invitation is unfortunately no longer valid.'
    }
  },
  uploadMediaItem: {
    error: {
      title: 'Image could not be uploaded!',
      content: 'Please try again at a later time.'
    }
  },
  saveMediaItem: {
    error: {
      title: 'Image could not be saved!',
      content: 'Please try again at a later time.'
    }
  },
  deleteMediaItem: {
    error: {
      title: 'Image could not be deleted!',
      content: 'Please try again at a later time.'
    }
  },
  unexpected: {
    error: {
      title: 'Oops, something went wrong!',
      content: 'Please try again at a later time.'
    }
  },
  createOrganizationKey: {
    missingEmailError: {
      title: 'Organization email missing!',
      content:
        'An organization email is required to generate keys. Please add your organization email to the organization.'
    },
    invalidEmailError: {
      title: 'Organization email is invalid!',
      content:
        'A valid email is required for key generation. Please check your organization email.'
    }
  },
  acceptDraft: {
    success: { title: 'Request successfully accepted!' }
  },
  rejectDraft: {
    success: { title: 'Request successfully rejected!' }
  },
  updateDraft: {
    success: { title: 'Request successfully updated!' }
  },
  platformAdminUsers: {
    enabledUpdated: { title: 'Account status updated successfully!' },
    roleUpdated: { title: 'Administrator role updated successfully!' },
    actionFailed: {
      title: 'Action failed',
      content: 'The administrative action could not be completed.'
    },
    lastAdminRemaining: {
      title: 'Action not possible',
      content: 'At least one active administrator must remain in the system.'
    },
    cannotDisableSelf: {
      title: 'Action not possible',
      content: 'You cannot disable your own account.'
    },
    cannotRevokeOwnAdminRole: {
      title: 'Action not possible',
      content: 'You cannot remove your own administrator role.'
    }
  },
  platformAdminOffers: {
    suspended: { title: 'Offer suspended successfully!' },
    unsuspended: { title: 'Offer unsuspended successfully!' },
    removed: { title: 'Offer removed successfully!' },
    actionFailed: {
      title: 'Action failed',
      content: 'The administrative action could not be completed.'
    }
  }
};
